import { ModelResolver } from '@app/app/rendering/mesh/model-resolver';
import { GetSatType } from '@app/engine/core/interfaces';
import { DEG2RAD, EpochUTC, PayloadStatus, SpaceObjectType, Sun, Vector3D } from '@ootk/src/main';
import { mat3, mat4, vec3 } from 'gl-matrix';
import { SplashScreen } from '../../app/ui/splash-screen';
import { ServiceLocator } from '../core/service-locator';
import { errorManagerInstance } from '../utils/errorManager';
import { OBJLoader } from './mesh/loaders/webgl-obj-loader';
import { MeshRegistry } from './mesh/mesh-registry';
import { MeshRenderer } from './mesh/mesh-renderer';
export class MeshManager {
    calculateNadirYaw_;
    currentMeshObject = {
        id: -1,
        position: { x: 0, y: 0, z: 0 },
        sccNum: '',
        inSun: 0,
        model: null,
        isRotationStable: false,
        rotation: { x: 0, y: 0, z: 0 },
    };
    mvMatrix_;
    nMatrix_;
    modelResolver_;
    meshRegistry_;
    meshRenderer_;
    gl_;
    isReady;
    draw(pMatrix, camMatrix, tgtBuffer = null) {
        this.meshRenderer_.draw(pMatrix, camMatrix, tgtBuffer);
    }
    drawOcclusion(pMatrix, camMatrix, occlusionPrgm, tgtBuffer) {
        this.meshRenderer_.drawOcclusion(pMatrix, camMatrix, occlusionPrgm, tgtBuffer);
    }
    init(gl) {
        try {
            if (settingsManager.disableUI || settingsManager.isDrawLess || settingsManager.noMeshManager) {
                return;
            }
            this.gl_ = gl;
            this.modelResolver_ = new ModelResolver();
            this.meshRegistry_ = new MeshRegistry();
            this.meshRenderer_ = new MeshRenderer(this, gl);
            this.meshRegistry_.registerLoader(new OBJLoader(), ['.obj']);
            settingsManager.selectedColor = [0.0, 0.0, 0.0, 0.0];
            // Changes Loading Screen Text
            SplashScreen.loadStr(SplashScreen.msg.models);
            this.isReady = true;
        }
        catch {
            errorManagerInstance.warn('Meshes failed to load!');
        }
    }
    setCurrentModel(model) {
        if (this.currentMeshObject.model?.name !== model.name) {
            this.currentMeshObject.rotation = {
                x: 0,
                y: 0,
                z: 0,
            };
        }
        this.currentMeshObject.model = model;
    }
    update(selectedDate, sat) {
        if (!sat.isSatellite() && !sat.isMissile()) {
            return;
        }
        this.updateModel_(selectedDate, sat);
        if (!this.currentMeshObject.model?.mesh && this.currentMeshObject.model?.name) {
            const meshName = this.currentMeshObject.model.name;
            this.meshRegistry_.load(meshName, `${settingsManager.installDirectory}meshes/${meshName}.obj`, this.gl_);
        }
        const posData = ServiceLocator.getDotsManager().positionData;
        const position = {
            x: posData[sat.id * 3],
            y: posData[sat.id * 3 + 1],
            z: posData[sat.id * 3 + 2],
        };
        let drawPosition = [position.x, position.y, position.z];
        // We need to avoid zero values. They break mat4.targetTo
        drawPosition = drawPosition.map((coord) => coord / 1e11);
        // Move the mesh to its location in world space
        this.mvMatrix_ = mat4.create();
        mat4.translate(this.mvMatrix_, this.mvMatrix_, drawPosition);
        if (this.currentMeshObject.isRotationStable ||
            (sat.type === SpaceObjectType.PAYLOAD && sat.status === PayloadStatus.OPERATIONAL) ||
            sat.isStable) {
            // Rotate the Satellite to Face Nadir if needed
            this.updateRotationToNadir_(drawPosition);
        }
        else {
            // If unstable then add some tumbling rotation
            const dt = ServiceLocator.getRenderer().dtAdjusted;
            this.currentMeshObject.rotation.z = (this.currentMeshObject.rotation.z + (dt * 10)) % 360;
            this.currentMeshObject.rotation.y = sat.inclination;
        }
        if (this.currentMeshObject.rotation?.x) {
            mat4.rotateX(this.mvMatrix_, this.mvMatrix_, this.currentMeshObject.rotation.x * DEG2RAD);
        }
        if (this.currentMeshObject.rotation?.y) {
            mat4.rotateY(this.mvMatrix_, this.mvMatrix_, this.currentMeshObject.rotation.y * DEG2RAD);
        }
        if (this.currentMeshObject.rotation?.z) {
            mat4.rotateZ(this.mvMatrix_, this.mvMatrix_, this.currentMeshObject.rotation.z * DEG2RAD);
        }
        // Allow Manual Rotation of Meshes
        mat4.rotateX(this.mvMatrix_, this.mvMatrix_, settingsManager.meshRotation.x * DEG2RAD);
        mat4.rotateY(this.mvMatrix_, this.mvMatrix_, settingsManager.meshRotation.y * DEG2RAD);
        mat4.rotateZ(this.mvMatrix_, this.mvMatrix_, settingsManager.meshRotation.z * DEG2RAD);
        // Assign the normal matrix the opposite of the this.mvMatrix_
        this.nMatrix_ = mat3.create();
        mat3.normalFromMat4(this.nMatrix_, this.mvMatrix_);
    }
    updateRotationToNadir_(drawPosition) {
        const catalogManagerInstance = ServiceLocator.getCatalogManager();
        const sat = catalogManagerInstance.getObject(this.currentMeshObject.id, GetSatType.POSITION_ONLY);
        if (sat === null) {
            return;
        }
        // Calculate a position to look at along the satellite's velocity vector
        const lookAtPos = [drawPosition[0] + sat.velocity.x, drawPosition[1] + sat.velocity.y, drawPosition[2] + sat.velocity.z];
        let up;
        if (sat.isSatellite()) {
            up = vec3.normalize(vec3.create(), drawPosition);
        }
        else {
            // Up is perpendicular to the velocity vector
            up = vec3.cross(vec3.create(), vec3.fromValues(0, 1, 0), vec3.fromValues(sat.velocity.x, sat.velocity.y, sat.velocity.z));
        }
        mat4.targetTo(this.mvMatrix_, drawPosition, lookAtPos, up);
    }
    updateModel_(selectedDate, obj) {
        try {
            this.currentMeshObject.id = typeof obj?.id !== 'undefined' ? obj.id : -1;
            if (typeof this.currentMeshObject.id === 'undefined' || this.currentMeshObject.id === -1) {
                return;
            }
            if (settingsManager.modelsOnSatelliteViewOverride) {
                return;
            }
            this.updatePosition(obj.position);
            const pos = new Vector3D(obj.position.x, obj.position.y, obj.position.z);
            this.currentMeshObject.inSun = Sun.lightingRatio(pos, Sun.position(EpochUTC.fromDateTime(selectedDate)));
            this.currentMeshObject.isRotationStable = false;
            if (settingsManager.meshOverride) {
                if (typeof this.modelResolver_.modelMap[settingsManager.meshOverride] === 'undefined') {
                    errorManagerInstance.debug(`Mesh override not found: ${settingsManager.meshOverride}`);
                    settingsManager.meshOverride = null;
                }
                else {
                    this.currentMeshObject.model = this.meshRegistry_.get(settingsManager.meshOverride) ?? {
                        id: -1,
                        name: settingsManager.meshOverride,
                    };
                    return;
                }
            }
            const modelName = this.modelResolver_.resolve(obj);
            const resolvedMesh = this.meshRegistry_.get(modelName) ?? {
                id: -1,
                name: modelName,
            };
            this.setCurrentModel(resolvedMesh);
        }
        catch {
            // Don't Let meshManager break everything
        }
    }
    updatePosition(targetPosition) {
        this.currentMeshObject.position.x = targetPosition.x;
        this.currentMeshObject.position.y = targetPosition.y;
        this.currentMeshObject.position.z = targetPosition.z;
    }
}
